// Alternates (SuiteModel/Alternates.swift, spec §6.4): reads over `AlternateSet` (stored as JSON per set in
// `settings.alternates`; the type and `alternateSets()` are the core's, edit/storeEditor.ts). The writes — fork a scene or
// a folder, promote — are in edits.ts; `forkArrangement` is StoreEditor's.
import type * as A from '@automerge/automerge';
import { fragmentDocName } from '../edit/editProject.ts';
import type { AlternateSet } from '../edit/storeEditor.ts';
import { tree as buildTree, type ArrangementTreeNode } from '../model/arrangement.ts';
import { readFragment, type Arrangement } from '../model/documents.ts';

export type AlternateMember = AlternateSet['members'][number];

export const currentMember = (set: AlternateSet): AlternateMember | null => set.members.find((m) => m.id === set.currentMemberID) ?? null;
export const archivedMembers = (set: AlternateSet): AlternateMember[] => set.members.filter((m) => m.id !== set.currentMemberID);

/** `alternateSet(forNode:)`: the scene or folder set at a node, when it has alternates (for the card's badge). */
export const alternateSetForNode = (sets: AlternateSet[], nodeID: string | null | undefined): AlternateSet | null =>
  nodeID ? sets.find((s) => s.nodeID === nodeID && s.kind !== 'arrangement' && s.members.length > 1) ?? null : null;

/** `AlternatesError`. */
export class AlternatesError extends Error {
  constructor(readonly kind: 'notFound' | 'notAFolder', what = '') {
    super(kind === 'notFound' ? `Couldn't find ${what}` : 'Only a folder\'s contents can be forked as a folder');
    this.name = 'AlternatesError';
  }
}

/** `text(of:in:)`: a member's prose in reading order, for comparing alternates side by side (non-empty texts joined by a
 *  blank line). */
export function alternateText(docs: Map<string, A.Doc<unknown>>, arrangements: Arrangement[], set: AlternateSet, member: AlternateMember): string {
  const text = (fragmentID: string) => {
    const doc = docs.get(fragmentDocName(fragmentID));
    if (!doc) throw new AlternatesError('notFound', `fragment ${fragmentID}`);
    return readFragment(doc).text;
  };
  const arrangement = (id: string) => {
    const a = arrangements.find((x) => x.id === id);
    if (!a) throw new AlternatesError('notFound', `arrangement ${id}`);
    return a;
  };
  const find = (nodes: ArrangementTreeNode[], id: string): ArrangementTreeNode | null => {
    for (const n of nodes) {
      if (n.node.id === id) return n;
      const hit = find(n.children, id);
      if (hit) return hit;
    }
    return null;
  };
  const concatenated = (a: Arrangement, under: string | null) => {
    const parts: string[] = [];
    const walk = (nodes: ArrangementTreeNode[]) => {
      for (const n of nodes) {
        if (n.node.fragmentID !== undefined) { const t = text(n.node.fragmentID); if (t !== '') parts.push(t); }
        walk(n.children);
      }
    };
    let roots = buildTree(a);
    if (under !== null) { const hit = find(roots, under); roots = hit ? [hit] : []; }
    walk(roots);
    return parts.join('\n\n');
  };
  switch (set.kind) {
    case 'fragment':
      return member.fragmentID !== undefined ? text(member.fragmentID) : '';
    case 'folder':
      if (member.id === set.currentMemberID && set.arrangementID !== undefined && set.nodeID !== undefined) {
        return concatenated(arrangement(set.arrangementID), set.nodeID);
      }
      return member.arrangementID !== undefined ? concatenated(arrangement(member.arrangementID), null) : '';
    case 'arrangement':
      return member.arrangementID !== undefined ? concatenated(arrangement(member.arrangementID), null) : '';
  }
}

/** Where editing a member leads (`editAlternate`): a scene's fragment in the editor (null for the current one), or the
 *  arrangement that holds a folder's or arrangement's version. */
export function editTarget(set: AlternateSet, member: AlternateMember): { fragmentID: string | null } | { arrangementID: string } | null {
  if (set.kind === 'fragment') return member.fragmentID !== undefined ? { fragmentID: member.id === set.currentMemberID ? null : member.fragmentID } : null;
  const target = member.id === set.currentMemberID && set.kind === 'folder' ? set.arrangementID : member.arrangementID;
  return target !== undefined ? { arrangementID: target } : null;
}
